import React, { useEffect, useState } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import TaxInvoicePrintLayout from '../../shared/TaxInvoice/TaxInvoicePrintLayout';
import TaxInvoicePrintModal from '../../shared/TaxInvoice/TaxInvoicePrintModal';
import { normalizeTaxInvoiceData } from '../../shared/TaxInvoice/normalizeTaxInvoiceData';
import { formatDate, formatCurrency, getText, unwrapResourceData, resolveDocumentId } from '../../shared/TaxInvoice/taxInvoiceUtils';

const SaleInvoicePreview = ({ data, isLoading }) => {
    const [searchParams] = useSearchParams();
    const { id: routeId } = useParams();
    const preview = searchParams.get('preview');
    const [showPrintModal, setShowPrintModal] = useState(false);

    useEffect(() => {
        if (!data) return;
        if (preview) setShowPrintModal(true);
    }, [preview, data]);

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="text-center p-4">
                <h5>No data available</h5>
            </div>
        );
    }

    const handlePrint = () => setShowPrintModal(true);
    const handleClosePrintModal = () => setShowPrintModal(false);
    const invoiceData = unwrapResourceData(data);

    return (
        <div className="h-100 d-flex flex-column">
            <div className="d-flex justify-content-between mb-2">
                <div className="d-flex justify-content-start">
                    <h5 className="mb-0">Sale Invoice Details</h5>
                </div>
                <div className="d-flex justify-content-end">
                    <button className="btn btn-primary btn-sm no-print" onClick={handlePrint} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <i className="fas fa-print"></i>
                        Print Preview
                    </button>
                </div>
            </div>

            {/* Basic on-screen preview can be minimal; print modal renders full invoice */}
            <div className="table-responsive mt-2">
                <table className="table table-sm table-borderless" style={{ marginBottom: 0 }}>
                    <tbody>
                        <tr>
                            <td className="fw-bold">Invoice No:</td>
                            <td>{getText(data.invoice_number) || getText(data.number) || '-'}</td>
                            <td className="fw-bold">Invoice Date:</td>
                            <td>{formatDate(data.invoice_date || data.date)}</td>
                        </tr>
                        <tr>
                            <td className="fw-bold">Customer:</td>
                            <td>{getText(data.customer_name) || getText(data.customer?.label) || '-'}</td>
                            <td className="fw-bold">VAT No:</td>
                            <td>{getText(data.vat_number) || getText(data.customer?.vat_number) || '-'}</td>
                        </tr>
                        <tr>
                            <td className="fw-bold">Address:</td>
                            <td colSpan="3">
                                {(getText(data.address1) || '')}
                                {getText(data.address2) ? ` ${getText(data.address2)}` : ''}
                                {getText(data.country?.name) || data.country?.label ? `, ${getText(data.country?.name) || data.country?.label}` : ''}
                            </td>
                        </tr>
                        <tr>
                            <td className="fw-bold">Phone:</td>
                            <td>{getText(data.phone_number) || getText(data.customer?.phone) || '-'}</td>

                        </tr>
                        <tr>
                            <td className="fw-bold">Delivery Note(s):</td>
                            <td colSpan="3">
                                {Array.isArray(data.linked_delivery_notes) && data.linked_delivery_notes.length > 0
                                    ? data.linked_delivery_notes
                                        .map((dn) => dn.label || dn.invoice_number)
                                        .filter(Boolean)
                                        .join(', ')
                                    : (getText(data.delivery_note?.label) || getText(data.delivery_number) || '-')}
                            </td>
                        </tr>
                        <tr>
                            <td className="fw-bold">LPO No:</td>
                            <td>{getText(data.lpo_number) || '-'}</td>
                            <td className="fw-bold">LPO Date:</td>
                            <td>{formatDate(data.lpo_date)}</td>
                        </tr>
                        <tr>
                            <td className="fw-bold">Customer LPO:</td>
                            <td>{getText(data.customer_lpo) || '-'}</td>
                            <td></td>
                            <td></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* On-screen line items table (similar to CashMemo preview) */}
            <div className="table-responsive mt-4">
                {(() => {
                    const lineItems = Array.isArray(data.grouped_items)
                        ? data.grouped_items
                        : (Array.isArray(data.sale_invoice_items) ? data.sale_invoice_items : []);
                    return (
                        <table className="table table-bordered">
                            <thead>
                                <tr>
                                    <th className="text-nowrap p-3">Item Code</th>
                                    <th className="text-nowrap p-3">Item Name</th>
                                    <th className="text-nowrap p-3">Unit</th>
                                    <th className="text-nowrap p-3">Qty</th>
                                    <th className="text-nowrap p-3">Unit Price</th>
                                    <th className="text-nowrap p-3">Discount %</th>
                                    <th className="text-nowrap p-3">Amt Excl VAT</th>
                                    <th className="text-nowrap p-3">VAT %</th>
                                    <th className="text-nowrap p-3">VAT Amt</th>
                                    <th className="text-nowrap p-3">Amt Incl VAT</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lineItems && lineItems.length > 0 ? (
                                    lineItems.map((row, index) => {
                                        const code = getText(row.item?.item_code) || getText(row.item_code) || 'N/A';
                                        const name = getText(row.item_description) || 'N/A';
                                        const unit = getText(row.unit) || 'N/A';
                                        const qty = row.quantity ?? row.qty ?? 0;
                                        const price = row.unit_price ?? row.price ?? 0;
                                        const disc = row.discount_percentage ?? row.discount_percent ?? 0;
                                        const excl = (row.amount_excl_vat ?? row.amount_excluding_vat ?? row.line_amount ?? 0);
                                        const vatp = row.vat_percentage ?? row.vat_percent ?? 0;
                                        const vatAmt = row.vat_amount ?? 0;
                                        const incl = row.amount_incl_vat ?? row.line_total ?? (excl + vatAmt);
                                        return (
                                            <tr key={index}>
                                                <td className="text-nowrap p-3 align-middle">{code}</td>
                                                <td className="text-nowrap p-3 align-middle">{name}</td>
                                                <td className="p-3 align-middle">{unit}</td>
                                                <td className="p-3 align-middle">{qty}</td>
                                                <td className="p-3 align-middle">{formatCurrency(price)}</td>
                                                <td className="p-3 align-middle">{disc || 0}%</td>
                                                <td className="p-3 align-middle">{formatCurrency(excl)}</td>
                                                <td className="p-3 align-middle">{vatp || 0}%</td>
                                                <td className="p-3 align-middle">{formatCurrency(vatAmt)}</td>
                                                <td className="p-3 align-middle">{formatCurrency(incl)}</td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="10" className="text-center">No items added</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    );
                })()}
            </div>

            {/* Compact totals summary on screen (same fields as print) */}
            <div className="table-responsive mt-2">
                <table className="table table-sm table-borderless" style={{ marginBottom: 0 }}>
                    <tbody>
                        <tr>
                            <td className="fw-bold">Gross Total:</td>
                            <td>{formatCurrency(data.gross_total)}</td>
                            <td className="fw-bold">Discount %:</td>
                            <td>{data.discount_percentage || '0.00'}%</td>
                        </tr>
                        <tr>
                            <td className="fw-bold">Discount Amount:</td>
                            <td>{formatCurrency(data.discount_amount)}</td>
                            <td className="fw-bold">Sub Total:</td>
                            <td>{formatCurrency(data.sub_total)}</td>
                        </tr>
                        <tr>
                            <td className="fw-bold">VAT Amount:</td>
                            <td>{formatCurrency(data.vat_amount)}</td>
                            <td className="fw-bold">Net Total:</td>
                            <td className="fw-bold">{formatCurrency(data.net_total)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <TaxInvoicePrintModal
                open={showPrintModal}
                onClose={handleClosePrintModal}
                title="Sale Invoice Print"
                documentType="sale_invoice"
                documentId={resolveDocumentId(data, routeId)}
            >
                <TaxInvoicePrintLayout data={normalizeTaxInvoiceData(invoiceData, 'sale_invoice')} />
            </TaxInvoicePrintModal>
        </div>
    );
};

export default SaleInvoicePreview;
